"use client";

import { useQuery } from "@tanstack/react-query";
import { paginate, type Group } from "@theta/api-client";
import { thetaApi, pageOf } from "@/lib/api/theta";

/**
 * The viewer's own groups — `GET /groups?member=me&sort=favourite`.
 *
 * NOT A ROUTE OF ITS OWN ANY MORE. `/theta/my-groups` was the same query with
 * a different URL; §2.14 folds it into the collection as a filter, which is
 * also why "my groups" and "all groups" can no longer disagree about what a
 * group document contains.
 *
 * THE ORDER IS THE SERVER'S (§1.4). `sort=favourite` puts favourites first and
 * then orders by the latest post (OSO-676; it launched as favourites-then-
 * name) — the sidebar's rule, moved to the one place that can apply it across
 * a page boundary. The old hook sorted the array it happened to have, which
 * was every group; that is not available under cursor paging and re-sorting
 * one page would silently reorder a partial list.
 *
 * EVERY PAGE IS WALKED HERE, once, rather than by each of the five screens
 * that render a picker or a sidebar off this list. A person's memberships are
 * bounded by how many groups they joined, so this is one request for almost
 * everybody and the alternative — a `hasMore` each caller has to thread — is
 * five chances to stop at page one.
 */
/**
 * `enabled` EXISTS FOR THE SIGNED-OUT READER (OSO-580). `GET /groups?member=me`
 * is an authenticated read: called without a session it answers 401, once per
 * mount, forever. Every screen that has always been behind the session leaves
 * this alone and gets the default; the one caller that renders for a signed-out
 * visitor — the open invitation link — passes `!!me`.
 */
export function useMyGroups({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    enabled,
    /**
     * A KEY OF ITS OWN, deliberately not `groupsKey(...)`: this resolves to a
     * FLAT `Group[]` and `useV1Groups` resolves to react-query's
     * `InfiniteData`, so sharing a key would hand one hook the other's shape.
     * `invalidateGroup` drops both — it invalidates the `["v1","groups"]`
     * prefix.
     */
    queryKey: ["v1", "groups", "mine"],
    queryFn: async () => {
      const groups: Group[] = [];
      for await (const group of paginate<Group>((cursor) =>
        thetaApi.http
          .GET("/groups", {
            params: {
              query: {
                member: "me" as const,
                sort: "favourite" as const,
                limit: 100,
                ...(cursor ? { cursor } : {}),
              },
            },
          })
          .then(pageOf<Group>),
      )) {
        groups.push(group);
      }
      return groups;
    },
    staleTime: 5 * 60 * 1000,
  });
}
